"use client";
import Button from "@/components/Button";
import ModalComponent, { useModal } from "@/components/Modal";
import PaymentModal from "@/components/PaymentModal";
import SubscriptionDetailNew from "@/components/SubscriptionDetailNew";
import { useUserContext } from "@/context/UserContext";
import { cn } from "@/lib/utils";
import { ISubscription } from "@/types/subscription";
import { useFeatures } from "@/vars/subscriptions";
import {
  ArrowRightIcon,
  CheckIcon,
  StarsIcon,
  TicketIcon,
  XIcon,
} from "lucide-react";
import { useSearchParams } from "next/navigation";
import { Fragment, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import useSWR from "swr";

const sections = {
  monthly: 1,
  annually: 12,
};

const currencies = {
  idr: "IDR",
  usd: "USD",
};

export default function AllSubscription({ noAction }: { noAction?: boolean }) {
  const { t } = useTranslation();
  const query = useSearchParams();
  const paymentModal = useModal();
  const voucher = query.get("voucher");
  const expandPackageModal = useModal();

  const { user, status: userStatus, authModal } = useUserContext();

  const { groups } = useFeatures();

  const userSubs = useMemo(() => {
    if (user && user.user_subscription?.still_active) {
      return user?.user_subscription?.subscription as ISubscription;
    }
    return null;
  }, [user]);

  const isUpgrade = userSubs && !noAction && query.has("upgrade");

  const { data, isLoading, error } = useSWR(
    () => "/subscriptions" + (isUpgrade ? "?upgrade=true" : "")
  );

  const [items, setItems] = useState([]);

  const [section, setSection] = useState("annually");
  const [currency, setCurrency] = useState(query.get("currency") || "idr");

  const benefits = {
    free: [
      t("limited_shortener_links"),
      t("limited_microsite_quota"),
      t("create_qrcode_for_links"),
      t("basic_analytics"),
      t("sell_product_using_sid_shop"),
    ],
    lite: [
      t("more_shortener_links_quota"),
      t("can_change_microsite_name"),
      t("meta_description_microsite_seo"),
      t("lifetime_link_visitor"),
      t("no_ads"),
    ],
    starter: [
      <Trans i18nKey="all_feature_from_free">
        All feature from <strong>Free</strong>
      </Trans>,
      t("hide_powered_by_sid"),
      t("lock_microsite_component"),
      t("limited_shortener_name"),
      t("qr_code_customization"),
    ],
    pro: [
      <Trans i18nKey="all_feature_from_starter">
        All Feature from <strong>Starter</strong>
      </Trans>,
      t("custom_domain_for_microsite"),
      t("more_qr_code_customization_style"),
      t("more_analytics_retention"),
      // t("shop_item_review"),
    ],
    business: [
      <Trans i18nKey="all_feature_from_pro">
        All Feature from <strong>Pro</strong>
      </Trans>,
      t("use_custom_subdomain"),
      t("less_transaction_fee"),
      t("webhook_notification"),
      // t("shop_item_review"),
    ],
    ultimate: [
      // <Trans i18nKey="all_feature_from_pro">
      //   All Feature from <strong>Pro</strong>
      // </Trans>,
      <Trans i18nKey="all_features_from_business">
        All Features from <strong>Business</strong>
      </Trans>,
      t("bigger_quota_for_shortener"),
      t("bigger_quota_for_microsite"),
      t("use_custom_subdomain"),
    ],
  };

  const subscriptionItems = useMemo(() => {
    const items = groups.map((group) => {
      let item = null;
      let upgradeable = false;
      const interval = sections[section];
      const found = data?.data?.find(
        (m) => m.group === group && m.interval_count === interval
      );
      if (found) {
        item = found;
        if (section === "annually") {
          item.monthly =
            data?.data?.find(
              (m) => m.group === group && m.interval_count === 1
            ) || null;
        }

        if (isUpgrade && user && user.user_subscription) {
          if (
            user.user_subscription.status === "ACTIVE" &&
            user.user_subscription.still_active &&
            item?.prices[currency] > userSubs?.prices[currency]
          ) {
            upgradeable = true;
          }
        }
      }

      return {
        isLoading,
        group,
        item,
        recommended: group === "pro",
        upgradeable,
      };
    });

    return items;
  }, [data, isLoading, error, currency, section, isUpgrade, userSubs, groups]);

  const onChoosePackage = (item) => {
    if (userStatus === "authenticated") {
      setItems([
        {
          subscription_id: item.id,
          qty: 1,
          type: isUpgrade ? "UPGRADE" : "SUBSCRIPTION",
        },
      ]);
      paymentModal.setOpen(true);
    } else {
      authModal.open({ next: "/subscription" });
    }
  };

  return (
    <>
      <div className="flex justify-center items-center my-10 w-full">
        <div className="flex gap-2 mx-auto w-full lg:w-auto font-worksans lg:text-xl">
          {Object.keys(sections).map((key) => (
            <button
              className={cn(
                "transition-all",
                "flex-1 lg:flex-initial",
                "px-6 py-3 border rounded-full font-semibold",
                key === section
                  ? "bg-primary text-white border-primary"
                  : "border border-primary bg-background text-primary"
              )}
              onClick={() => setSection(key)}
              key={key}
            >
              {t(key)}
            </button>
          ))}
        </div>
      </div>

      {userSubs && isUpgrade ? (
        <>
          <div className="top-[5rem] z-40 sticky bg-blue-600 mb-4 p-4 lg:p-6 py-2 rounded-xl text-white text-center">
            <h2 className="font-bold lg:text-lg">
              <Trans i18nKey="upgrade_package">Upgrade Paket</Trans>
            </h2>
            <p>
              <Trans i18nKey="upgrade_package_desc">
                Upgrade paket subscription anda sekarang
              </Trans>
            </p>
          </div>
          <div className="mb-4 p-4 lg:p-6 py-2 border rounded-xl text-foreground text-center">
            <Trans i18nKey="current_active_package">
              Paket anda yang sedang aktif saat ini
            </Trans>
            : <strong>{userSubs.name}</strong>
          </div>
        </>
      ) : (
        <Fragment />
      )}

      <div className="flex justify-end w-full">
        <div className="flex items-center gap-2 font-worksans text-base">
          <p className="pr-1 font-medium">{t("currency")} :</p>
          {Object.keys(currencies).map((key) => (
            <button
              className={cn(
                "transition-all",
                "px-4 py-0.5 border border-shop rounded-full font-semibold",
                key === currency
                  ? "bg-primary text-white border-primary"
                  : "border border-primary bg-background text-primary"
              )}
              onClick={() => setCurrency(key)}
              key={key}
            >
              {currencies[key]}
            </button>
          ))}
        </div>
      </div>

      <div className="py-2">
        <div className="flex flex-wrap -m-2">
          <div className="flex flex-wrap lg:flex-nowrap w-full">
            {subscriptionItems.map((item) => (
              <SubscriptionDetailNew
                key={item.group}
                currency={currency}
                section={section}
                benefits={benefits[item.group]}
                {...item}
                noAction={noAction}
                recommended={item.recommended}
                onChoose={onChoosePackage}
                isUpgrade={isUpgrade}
              />
            ))}
          </div>
          {!noAction && (
            <>
              <div className="p-2 w-full">
                <div className="p-4 border rounded-xl w-full">
                  <h3 className="flex items-center font-worksans font-bold text-foreground text-xl">
                    <StarsIcon className="mr-2 size-4" />{" "}
                    <Trans i18nKey="custom_business_package">
                      Opsi Custom &amp; Paket Khusus Bisnis
                    </Trans>
                  </h3>
                  <p className="mb-2 text-foreground/80">
                    <Trans i18nKey="need_custom_package">
                      Butuh paket lain-nya yang tidak ada pada paket di atas?
                    </Trans>
                  </p>
                  <Button
                    variant="dark"
                    roundFull
                    className="flex items-center"
                    onClick={expandPackageModal.open}
                  >
                    <Trans i18nKey="explore_other_packages">
                      Jelajahi paket lainnya
                    </Trans>{" "}
                    <ArrowRightIcon className="-mr-1 ml-2 size-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <p className="text-foreground/60 text-sm text-center italic">
        <Trans i18nKey="subscription_fee_note">
          Biaya di atas Belum termasuk biaya payment gateway dan pajak.
        </Trans>
      </p>

      {voucher && (
        <div className="right-0 bottom-0 left-0 z-50 fixed flex sm:p-12 px-12 pb-32 w-full">
          <div className="flex items-center bg-white/50 shadow-2xl backdrop-blur -m-1 mx-auto p-4 rounded-full w-full sm:w-auto text-orange-600">
            <TicketIcon className="inline-block size-6 -rotate-45" />
            <div className="flex sm:flex-row flex-col flex-1 sm:items-center pl-2">
              <span className="mr-2">
                <Trans i18nKey="you_are_use_code_voucher">
                  You are use code voucher
                </Trans>
              </span>
              <strong className="bg-orange-100/50 backdrop-blur px-4 py-1 rounded-full text-orange-600">
                {voucher}
              </strong>
            </div>
          </div>
        </div>
      )}

      <PaymentModal
        items={items}
        currency={currency}
        modalProp={paymentModal}
        voucher={voucher}
      />

      <ModalComponent {...expandPackageModal} disabled className="!max-w-4xl">
        <ExpandPackage
          setSection={setSection}
          section={section}
          data={data}
          isUpgrade={isUpgrade}
          user={user}
          currency={currency}
          setCurrency={setCurrency}
          userSubs={userSubs}
          isLoading={isLoading}
          error={error}
          benefits={benefits}
          onChoosePackage={onChoosePackage}
          modalProp={expandPackageModal}
        />
      </ModalComponent>
    </>
  );
}

function ExpandPackage({
  section,
  setSection,
  data,
  isUpgrade,
  user,
  currency,
  setCurrency,
  userSubs,
  isLoading,
  error,
  benefits,
  onChoosePackage,
  modalProp,
}) {
  const { t } = useTranslation();
  const subsGroupData = useSWR("/subscriptions/group");
  const { features } = useFeatures(["ultimate"]);

  const subscriptionGroup = useMemo(() => {
    let groups = [];
    if (subsGroupData.data?.data) {
      groups = [
        ...subsGroupData.data.data.subscription_additional_group.map(
          (m) => m.name
        ),
      ];
    }
    return groups;
  }, [subsGroupData.data, subsGroupData.isLoading]);

  const subscriptionItems = useMemo(() => {
    const items = subscriptionGroup.map((group) => {
      let item = null;
      let upgradeable = false;
      const interval = sections[section];
      const found = data?.data?.find(
        (m) => m.group === group && m.interval_count === interval
      );
      if (found) {
        item = found;
        if (section === "annually") {
          item.monthly =
            data?.data?.find(
              (m) => m.group === group && m.interval_count === 1
            ) || null;
        }

        if (isUpgrade && user && user.user_subscription) {
          if (
            user.user_subscription.status === "ACTIVE" &&
            user.user_subscription.still_active &&
            item?.prices[currency] > userSubs?.prices[currency]
          ) {
            upgradeable = true;
          }
        }
      }

      return {
        isLoading,
        group,
        item,
        recommended: group === "pro",
        upgradeable,
      };
    });

    return items;
  }, [
    data,
    isLoading,
    error,
    currency,
    section,
    isUpgrade,
    userSubs,
    subscriptionGroup,
  ]);

  return (
    <>
      <button
        className="top-2 lg:-top-0 right-2 lg:-right-12 absolute flex justify-center items-center bg-white rounded-full size-10"
        onClick={modalProp.close}
      >
        <XIcon className="size-4" />
      </button>
      <div className="-m-4 p-4 h-[95dvh] overflow-x-hidden overflow-y-auto">
        <div className="flex lg:flex-row flex-col -mx-3 lg:divide-x">
          <div className="flex flex-col flex-1 justify-center px-3 pb-10 lg:pb-0">
            <h2 className="font-montserrat font-bold text-xl">
              <Trans i18nKey="other_subscription_package">
                Paket Berlangganan Lainnya
              </Trans>
            </h2>
            <div className="flex flex-col gap-3 mt-2 text-foreground/70">
              <Trans i18nKey="custom_business_package_info">
                <p>
                  Cek paket berlangganan lainnya yang tersedia. Kamu bisa
                  memilih paket berlangganan yang sesuai dengan kebutuhan
                  bisnismu.
                </p>
                <p>
                  Atau tidak ada paket yang sesuai dengan kebutuhan bisnis kamu?
                </p>
                <p>
                  Hubungi kami di{" "}
                  <a href="mailto:sales@s.id" className="underline">
                    sales@s.id
                  </a>{" "}
                  Untuk permintaan lebih lanjut.
                </p>
              </Trans>
            </div>
          </div>
          <div className="flex-1 px-3">
            <div className="flex gap-2 mb-3 w-full font-worksans">
              {Object.keys(sections).map((key) => (
                <button
                  className={cn(
                    "transition-all",
                    "flex-1",
                    "py-2 px-4 border rounded-full font-semibold",
                    key === section
                      ? "bg-primary text-white border-primary"
                      : "border border-primary bg-background text-primary"
                  )}
                  onClick={() => setSection(key)}
                  key={key}
                >
                  {t(key)}
                </button>
              ))}
            </div>
            <div className="flex justify-end w-full">
              <div className="flex items-center gap-2 font-worksans text-base">
                <p className="pr-1 font-medium">{t("currency")} :</p>
                {Object.keys(currencies).map((key) => (
                  <button
                    className={cn(
                      "transition-all",
                      "px-4 py-0.5 border border-shop rounded-full font-semibold",
                      key === currency
                        ? "bg-primary text-white border-primary"
                        : "border border-primary bg-background text-primary"
                    )}
                    onClick={() => setCurrency(key)}
                    key={key}
                  >
                    {currencies[key]}
                  </button>
                ))}
              </div>
            </div>
            {subscriptionItems.map((item) => (
              <SubscriptionDetailNew
                key={item.group}
                currency={currency}
                section={section}
                benefits={benefits[item.group]}
                {...item}
                noAction={false}
                recommended={item.recommended}
                onChoose={onChoosePackage}
                isUpgrade={isUpgrade}
              />
            ))}
          </div>
        </div>
        <div className="font-worksans font">
          <h3 className="mb-3 font-montserrat font-bold">
            <Trans i18nKey="landing:subscription_comparison_heading">
              Features &amp; Comparison
            </Trans>
          </h3>
          {features
            .filter((m) => !m.disabled)
            .map((item, i) => (
              <div
                key={i}
                className="flex hover:bg-slate-100 w-[80rem] lg:w-full overflow-hidden transition-all"
              >
                <div
                  className={cn(
                    "p-4 w-9/12 shrink-0",
                    item.isChild ? "border-t-none" : "border-t"
                  )}
                >
                  {item.isChild && <>&mdash;</>}{" "}
                  {t(`subscription:features.${item.name}`)}
                  {item.isNew && (
                    <span className="bg-orange-500 ml-2 px-1 py-0.5 rounded font-semibold text-white text-xs">
                      New
                    </span>
                  )}
                  {item.isBeta && (
                    <span className="bg-orange-500 ml-2 px-1 py-0.5 rounded font-semibold text-white text-xs">
                      Beta
                    </span>
                  )}
                </div>
                <div className="flex w-3/12">
                  {item.features.map((feature, idx) => (
                    <div
                      className={cn(
                        "p-4 flex-1 text-center shrink-0 border-l",
                        item.isChild ? "border-t-none" : "border-t"
                      )}
                      key={idx}
                    >
                      {feature === -1 ? (
                        <div className="inline-block bg-gray-200 my-1 mt-3 rounded w-[40px] h-4" />
                      ) : typeof feature === "string" ? (
                        feature
                      ) : feature === 2 ? (
                        <>
                          <div className="relative flex justify-center items-center bg-blue-500 mx-auto p-1 rounded-full size-6 text-white">
                            <CheckIcon className="inline-block text-white" />{" "}
                            <strong className="-top-1 -right-2.5 absolute text-black">
                              *
                            </strong>
                          </div>
                        </>
                      ) : feature ? (
                        <div className="flex justify-center items-center bg-blue-500 mx-auto p-1 rounded-full size-6">
                          <CheckIcon className="inline-block text-white" />
                        </div>
                      ) : (
                        <XIcon className="inline-block opacity-30 w-5" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </>
  );
}
